import React, { ReactElement } from 'react'
import PropTypes from 'prop-types'
import { ariaProps, dataProps, onProps } from '@ds/react-utils'
import { TextEllipsis } from '../../../components/TextEllipsis'
import { useThemeStyles } from '../../../theming'
import type { ButtonOnClickHandler } from '../../../types'
import { AnchorTarget, anchorTargets } from '../../../variables'
import { ConditionalTag } from '../ConditionalTag'
import { HeadingLevel, headingLevels, BaseHeadingTag } from './BaseHeadingTag'
import styles from './styles'

export { headingLevels }
export type { HeadingLevel }

export type HeadingDisplayLevel = (typeof headingDisplayLevels)[number]
export const headingDisplayLevels = [
  'heading-xxs',
  'heading-xs',
  'heading-s',
  'heading-m',
  'heading-l',
  'heading-xl',
  'display-xs',
  'display-s',
  'display-m',
  'display-l',
  'section-headline',
  'body-m',
] as const

export type HeadingTarget = AnchorTarget

function isString(value: string | ReactElement): value is string {
  return typeof value === 'string'
}

const getDisplayLevel = (
  displayLevel?: HeadingDisplayLevel,
  level?: HeadingLevel,
) => {
  if (displayLevel) return displayLevel
  switch (level) {
    case '1':
      return 'heading-xl'
    case '2':
      return 'heading-l'
    case '3':
      return 'heading-m'
    case '4':
      return 'heading-s'
    case '5':
      return 'heading-xs'
    case '6':
      return 'heading-xs'
    default:
      return 'heading-xxs'
  }
}

export interface BaseHeadingProps {
  /**
   * Accepts custom data attributes.
   */
  'data-.*'?: string
  'data-qa'?: string
  /**
   * An optional display level (i.e. importance indicated by size / style / casing)
   * that may differ from the semantic HTML section heading level.
   */
  displayLevel?: HeadingDisplayLevel
  /**
   * If href is provided then it will render an anchor, otherwise a button.
   */
  href?: string
  /**
   * A unique string identifier attribute for the Heading component.
   */
  id?: string
  /**
   * The semantic HTML heading level.
   * See the [Semantic Headings accessibility documentation](https://docusign.atlassian.net/wiki/display/ACC/Semantic+Headings) for more info.
   */
  level: HeadingLevel
  /**
   * A number, that if provided, wraps the text in a TextEllipsis component with the lines clamped to the provided number.
   */
  maxLines?: number
  /**
   * The function to call when a 'click' event is fired.
   */
  onClick?: ButtonOnClickHandler
  /**
   * The relationship of the linked URL of a anchor as space-separated link types.
   *
   * Reference: https://developer.mozilla.org/en-US/docs/Web/HTML/Link_types
   */
  rel?: string
  /**
   * Sets tab order for the heading element.
   */
  tabIndex?: number
  /**
   * The HTML link target (if an href is _not_ provided, this is ignored).
   * When target="_blank" use rel="noreferrer" or rel="noopener" to avoid the vulnerability.
   */
  target?: AnchorTarget
  /**
   * The text to be displayed.
   */
  text: string | React.ReactElement
}

export function BaseHeading({
  'data-qa': dataQa,
  displayLevel,
  href,
  id,
  level,
  maxLines,
  onClick,
  rel,
  target,
  text,
  ...restProps
}: BaseHeadingProps) {
  const sx = useThemeStyles(styles)

  const displayLvl = getDisplayLevel(displayLevel, level)
  const baseHeadingStyles = [sx.Heading, sx[displayLvl]]

  const textEllipsisNode = maxLines && isString(text) && (
    <TextEllipsis
      lines={maxLines}
      data-qa={dataQa && `${dataQa}-text-ellipsis`}
    >
      {text}
    </TextEllipsis>
  )

  const linkStyles = [
    sx.Heading.link,
    displayLvl !== 'section-headline' && {
      letterSpacing: 'normal',
    },
    (href || onClick) && sx.Heading.linkCursor,
  ]

  return (
    <BaseHeadingTag
      {...ariaProps(restProps)}
      css={baseHeadingStyles}
      className="olv-heading olv-ignore-transform"
      data-qa={dataQa && `${dataQa}-tag`}
      id={id}
      level={level}
    >
      <ConditionalTag
        {...dataProps(restProps)}
        {...onProps(restProps)}
        css={linkStyles}
        data-qa={dataQa}
        forceElement="span"
        href={href}
        onClick={onClick}
        rel={rel}
        tabIndex={restProps.tabIndex}
        target={target}
      >
        {maxLines ? textEllipsisNode : text}
      </ConditionalTag>
    </BaseHeadingTag>
  )
}

BaseHeading.levels = headingLevels
BaseHeading.displayLevels = headingDisplayLevels
BaseHeading.targets = anchorTargets

BaseHeading.propTypes = {
  'data-.*': PropTypes.string,
  displayLevel: PropTypes.oneOf(headingDisplayLevels),
  href: PropTypes.string,
  id: PropTypes.string,
  level: PropTypes.oneOf(headingLevels).isRequired,
  maxLines: PropTypes.number,
  'on[A-Z].*': PropTypes.func,
  onClick: PropTypes.func,
  rel: PropTypes.string,
  tabIndex: PropTypes.number,
  target: PropTypes.oneOf(anchorTargets),
  text: PropTypes.node.isRequired,
}

BaseHeading.displayName = 'BaseHeading'
